import "es6-shim";

import React from "react";
import PropTypes from "prop-types";
import { ContentState, convertFromHTML, Editor as DraftEditor, EditorState, getDefaultKeyBinding, RichUtils } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import Icon from "../../../components/Icon";

const styleMap =
	{
		CODE: {
			backgroundColor: "rgba(0, 0, 0, 0.05)",
			fontFamily: "'Inconsolata', 'Menlo', 'Consolas', monospace",
			fontSize: 16,
			padding: 2
		}
	};

function getBlockStyle( block )
{
	switch ( block.getType() )
	{
		case "blockquote":
			return "blockquote";
		default:
			return null;
	}
} // function getBlockStyle(...)

class StyleButton extends React.Component
{
	constructor( props )
	{
		super( props );
		
		this.onToggle = ( e ) =>
		{
			e.preventDefault();
			this.props.onToggle( this.props.style );
		};
		
	} // function constructor(...)
	
	render()
	{
		let className = "btn btn-small btn-flat normal-text white-text margin-right";
		if ( this.props.active )
		{
			className += " primary-color";
		}
		else
		{
			className += " secondary-color";
		}
		
		return (
			<span className={className} onMouseDown={this.onToggle}>
		        {this.props.label}
		      </span>
		);
		
	} // function render()
	
} // class

const BLOCK_TYPES = [
	{ label: "H1", style: "header-one" },
	{ label: "H2", style: "header-two" },
	{ label: "H3", style: "header-three" },
	{ label: "H4", style: "header-four" },
	{ label: "H5", style: "header-five" },
	{ label: "H6", style: "header-six" },
	{ label: <Icon iconName={"quote"}/>, style: "blockquote" },
	{ label: <Icon iconName={"unorderedList"}/>, style: "unordered-list-item" },
	{ label: <Icon iconName={"orderedList"}/>, style: "ordered-list-item" },
	{ label: "Code Block", style: "code-block" }
];

const BlockStyleControls = ( props ) =>
{
	const { editorState, controlTypes = null } = props;
	const controls = controlTypes === null ? BLOCK_TYPES : controlTypes;
	
	const selection = editorState.getSelection();
	const blockType = editorState
		.getCurrentContent()
		.getBlockForKey( selection.getStartKey() )
		.getType();
	
	return (
		<span className="editor-controls">
			{
				controls.map( ( type ) =>
					<StyleButton
						key={`${type.style}_${props.additionalKey}`}
						active={type.style === blockType}
						label={type.label}
						onToggle={props.onToggle}
						style={type.style}
					/>
				)
			}
		</span>
	);
};

const INLINE_STYLES = [
	{ label: <Icon iconName={"bold"}/>, style: "BOLD" },
	{ label: <Icon iconName={"italic"}/>, style: "ITALIC" },
	{ label: <Icon iconName={"underlined"}/>, style: "UNDERLINE" }
	// {label: 'Monospace', style: 'CODE'},
];

const InlineStyleControls = ( props ) =>
{
	const { controlTypes = null } = props;
	const controls = controlTypes === null ? INLINE_STYLES : controlTypes;
	
	const currentStyle = props.editorState.getCurrentInlineStyle();
	
	return (
		<span className="editor-controls">
			{
				controls.map( type =>
					<StyleButton
						key={`${type.style}_${props.additionalKey}`}
						active={currentStyle.has( type.style )}
						label={type.label}
						onToggle={props.onToggle}
						style={type.style}
					/>
				)
			}
		</span>
	);
	
};

export default class Editor extends React.Component
{
	constructor( props )
	{
		super( props );
		
		this.state = {
			isExpand: props.isHidden ? !props.isHidden : true
		};
		
		if ( typeof props.content !== "undefined" && props.content !== null )
		{
			const blocksFromHTML = convertFromHTML( props.content );
			
			if ( blocksFromHTML.contentBlocks !== null )
			{
				const state = ContentState.createFromBlockArray(
					blocksFromHTML.contentBlocks,
					blocksFromHTML.entityMap
				);
				
				this.state["editorState"] = EditorState.createWithContent( state );
			}
			else
			{
				this.state["editorState"] = EditorState.createEmpty();
			}
		}
		else
		{
			this.state["editorState"] = EditorState.createEmpty();
		}
		
		this.onFocus = this.onFocus.bind( this );
		this.onChange = this.onChange.bind( this );
		this.handleKeyCommand = this.handleKeyCommand.bind( this );
		this.mapKeyToEditorCommand = this.mapKeyToEditorCommand.bind( this );
		this.toggleBlockType = this.toggleBlockType.bind( this );
		this.toggleInlineStyle = this.toggleInlineStyle.bind( this );
		this.handleResetEditor = this.handleResetEditor.bind( this );
		this.handleExpand = this.handleExpand.bind( this );
		
	} // function constructor(...)
	
	/**
	 * Editor Content aktualisieren
	 *
	 * @param prevProps
	 * @param prevState
	 */
	componentDidUpdate( prevProps, prevState )
	{
		if ( this.props.content && this.props.content !== prevProps.content )
		{
			const blocksFromHTML = convertFromHTML( this.props.content );
			
			this.setState( {
				editorState: blocksFromHTML.contentBlocks
					? EditorState.createWithContent( ContentState.createFromBlockArray(
						blocksFromHTML.contentBlocks,
						blocksFromHTML.entityMap
					) )
					: EditorState.createEmpty()
			} );
		}
		
	} // function componentDidUpdate(...)
	
	/**
	 * Editor Content zurücksetzen
	 */
	componentWillUnmount()
	{
		this.handleResetEditor();
		
	} // function componentWillUnmount()
	
	/**
	 * Beim Klick in den Editor den Fokus setzen
	 */
	onFocus()
	{
		this.editor.focus();
		
	} // function onFocus()
	
	/**
	 * Beim Ändern den Inhalt merken und an die angeforderte Komponente zurückgeben
	 *
	 * @param editorState
	 */
	onChange( editorState )
	{
		this.setState( { editorState } );
		
		const contentState = editorState.getCurrentContent();
		const contentText = contentState.hasText() ? stateToHTML( contentState ) : "";
		this.props.onSubmit( contentText );
		
	} // function onChange(...)
	
	/**
	 * Merke die Tastatureingaben
	 *
	 * @param command
	 * @param editorState
	 * @return {string}
	 */
	handleKeyCommand( command, editorState )
	{
		const newState = RichUtils.handleKeyCommand( editorState, command );
		
		if ( newState )
		{
			this.onChange( newState );
			return "handled";
		}
		
		return "not-handled";
		
	} // function handleKeyCommand(...)
	
	/**
	 * Tab-Tastureingaben fangen
	 *
	 * @param e
	 * @return {*|?DraftEditorCommand}
	 */
	mapKeyToEditorCommand( e )
	{
		switch ( e.keyCode )
		{
			case 9: // TAB
				const newEditorState = RichUtils.onTab(
					e,
					this.state.editorState,
					4 /* maxDepth */
				);
				
				if ( newEditorState !== this.state.editorState )
				{
					this.onChange( newEditorState );
				}
				
				return;
			
			default:
				return getDefaultKeyBinding( e );
			
		}
		
	} // function mapKeyToEditorCommand(...)
	
	/**
	 * Block ein-/ausklappen
	 *
	 * @param blockType
	 */
	toggleBlockType( blockType )
	{
		this.onChange(
			RichUtils.toggleBlockType(
				this.state.editorState,
				blockType
			)
		);
		
	} // function toggleBlockType(...)
	
	/**
	 * Eingabemaske ein-/ausklappen
	 *
	 * @param inlineStyle
	 */
	toggleInlineStyle( inlineStyle )
	{
		this.onChange(
			RichUtils.toggleInlineStyle(
				this.state.editorState,
				inlineStyle
			)
		);
		
	} // function toggleInlineStyle(...)
	
	/**
	 * Handler um Editor Content zurücksetzen
	 */
	handleResetEditor()
	{
		if ( this.props.content )
		{
			const blocksFromHTML = convertFromHTML( this.props.content );
			
			this.setState( {
				editorState: blocksFromHTML.contentBlocks
					? EditorState.createWithContent( ContentState.createFromBlockArray(
						blocksFromHTML.contentBlocks,
						blocksFromHTML.entityMap
					) )
					: EditorState.createEmpty()
			} );
		}
		else
		{
			this.setState( { editorState: EditorState.createEmpty() } );
		}
		
	} // function handleResetEditor()
	
	/**
	 * Handler zum ein-/ausklappen
	 */
	handleExpand()
	{
		this.setState( { isExpand: !this.state.isExpand } );
		
	} // function handleExpand(
	
	render()
	{
		const { editorState } = this.state;
		
		let classNameInner = "editor";
		const contentState = editorState.getCurrentContent();
		
		if ( !contentState.hasText() )
		{
			if ( contentState.getBlockMap().first().getType() !== "unstyled" )
			{
				classNameInner += " hidePlaceholder";
			}
		}
		
		return <div className="Editor-root editor-with-header">
			<div className={"editor-header light-grey"}>
				<InlineStyleControls
					editorState={editorState}
					onToggle={this.toggleInlineStyle}
					controlTypes={this.props.inlineControlTypes}
					additionalKey={this.props.additionalKey}
				/>
				<BlockStyleControls
					editorState={editorState}
					onToggle={this.toggleBlockType}
					controlTypes={this.props.blockControlTypes}
					additionalKey={this.props.additionalKey}
				/>
				<div className={"actions actions-right position top"}>
					<button className="btn btn-small btn-flat transparent"
					        onClick={() => this.handleExpand()}>
						<Icon iconName={!this.state.isExpand ? "shrink" : "expand"}/>
					</button>
				</div>
			</div>
			{
				this.state.isExpand &&
				<div className={`editor-content ${this.props.className}`}>
					<div className={classNameInner} onClick={this.onFocus}>
						<DraftEditor
							blockStyleFn={getBlockStyle}
							customStyleMap={styleMap}
							editorState={editorState}
							handleKeyCommand={this.handleKeyCommand}
							// keyBindingFn={this.mapKeyToEditorCommand}
							keyBindingFn={this.mapKeyToEditorCommand}
							onChange={this.onChange}
							ref={( ref ) => this.editor = ref}
							spellCheck={true}
						/>
					</div>
				</div>
			}
		</div>;
		
	} // function render()
	
} // class

Editor.propTypes = {
	onSubmit: PropTypes.func.isRequired,
	additionalKey: PropTypes.string.isRequired,
	isHidden: PropTypes.bool,
	inlineControlTypes: PropTypes.array,
	blockControlTypes: PropTypes.array,
	className: PropTypes.string,
	content: PropTypes.string
};
